// Weekly working hours per barber (several ranges per day, e.g. a lunch break).
import { useEffect, useState } from 'react';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { Banner, Button, Field, Loading, Page, useToast } from '../ui/index.tsx';
import { useConflictRunner } from './conflicts.tsx';
import { fromMinutes, toMinutes, useSchedule } from './useSchedule.ts';

const DAYS: [number, string][] = [[1, 'จันทร์'], [2, 'อังคาร'], [3, 'พุธ'], [4, 'พฤหัสบดี'], [5, 'ศุกร์'], [6, 'เสาร์'], [0, 'อาทิตย์']];
type Range = { start: string; end: string };

export function Hours() {
  const { api } = useShop();
  const { go } = useShopPaths();
  const { data, reload } = useSchedule();
  const conflicts = useConflictRunner();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [barberId, setBarberId] = useState('');
  const [days, setDays] = useState<Record<number, Range[]>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !barberId && data.barbers[0]) setBarberId(data.barbers[0].id);
  }, [data, barberId]);

  useEffect(() => {
    if (!data || !barberId) return;
    const next: Record<number, Range[]> = {};
    for (const [wd] of DAYS) {
      next[wd] = data.weekly.filter((w) => w.barberId === barberId && w.weekday === wd)
        .sort((a, b) => a.startMin - b.startMin)
        .map((w) => ({ start: fromMinutes(w.startMin), end: fromMinutes(w.endMin) }));
    }
    setDays(next);
  }, [data, barberId]);

  if (!data) return <Page title="เวลาทำงาน" back={() => go('/staff/manage')}><Loading /></Page>;

  const update = (wd: number, i: number, key: keyof Range, v: string) =>
    setDays((d) => ({ ...d, [wd]: d[wd].map((r, j) => (j === i ? { ...r, [key]: v } : r)) }));
  const add = (wd: number) => setDays((d) => {
    const last = d[wd].at(-1);
    return { ...d, [wd]: [...d[wd], last ? { start: last.end, end: '20:00' } : { start: '10:00', end: '20:00' }] };
  });
  const remove = (wd: number, i: number) => setDays((d) => ({ ...d, [wd]: d[wd].filter((_, j) => j !== i) }));
  const copyToAll = (wd: number) => setDays((d) => Object.fromEntries(DAYS.map(([x]) => [x, d[wd].map((r) => ({ ...r }))])));

  async function save() {
    setError(null);
    const ranges: { weekday: number; startMin: number; endMin: number }[] = [];
    for (const [wd] of DAYS) {
      const list = (days[wd] ?? []).map((r) => ({ startMin: toMinutes(r.start), endMin: toMinutes(r.end, true) }));
      for (const r of list) {
        if (r.startMin === null || r.endMin === null) { setError('กรุณากรอกเวลาให้ครบ'); return; }
        if (r.endMin <= r.startMin) { setError('เวลาเลิกต้องหลังเวลาเริ่ม'); return; }
      }
      const sorted = [...list].sort((a, b) => (a.startMin as number) - (b.startMin as number));
      for (let i = 1; i < sorted.length; i++) {
        if ((sorted[i].startMin as number) < (sorted[i - 1].endMin as number)) { setError('ช่วงเวลาในวันเดียวกันซ้อนกัน'); return; }
      }
      for (const r of sorted) ranges.push({ weekday: wd, startMin: r.startMin as number, endMin: r.endMin as number });
    }
    setSaving(true);
    try {
      await conflicts.run((resolutions) => api.staff.setHours(barberId, ranges, resolutions), {
        onDone: () => { toast('บันทึกเวลาทำงานแล้ว', 'success'); void reload(); },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page title="เวลาทำงาน" back={() => go('/staff/manage')}>
      <Field label="ช่าง">
        <select value={barberId} onChange={(e) => setBarberId(e.target.value)}>
          {data.barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </Field>
      {error && <Banner kind="error">{error}</Banner>}
      <div className="list">
        {DAYS.map(([wd, label]) => (
          <div key={wd} className="card" data-testid={`day-${wd}`}>
            <div className="row-between" style={{ marginBottom: 6 }}>
              <div className="card-title">{label}</div>
              {(days[wd] ?? []).length === 0 ? <span className="muted small">ปิด</span> : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyToAll(wd)}>ใช้กับทุกวัน</button>
              )}
            </div>
            {(days[wd] ?? []).map((r, i) => (
              <div key={i} className="row" style={{ marginBottom: 6 }}>
                <input type="time" step={300} value={r.start} aria-label={`${label} เริ่ม ${i + 1}`} onChange={(e) => update(wd, i, 'start', e.target.value)}
                  style={{ minHeight: 40, flex: 1 }} />
                <span>–</span>
                <input type="time" step={300} value={r.end} aria-label={`${label} เลิก ${i + 1}`} onChange={(e) => update(wd, i, 'end', e.target.value)}
                  style={{ minHeight: 40, flex: 1 }} />
                <button type="button" className="icon-btn" aria-label={`ลบช่วงเวลา ${label} ${i + 1}`} onClick={() => remove(wd, i)}>✕</button>
              </div>
            ))}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => add(wd)}>＋ เพิ่มช่วงเวลา</button>
          </div>
        ))}
      </div>
      <div style={{ height: 16 }} />
      <Button block loading={saving} onClick={() => void save()}>บันทึกเวลาทำงาน</Button>
      {conflicts.dialog}
    </Page>
  );
}
